<script setup lang="ts">
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, h, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import type { CustomerCase as Case } from '@/types/generated'
  import { ROUTES } from '@/router/routes'
  import { ArrowLongRightIcon, EllipsisVerticalIcon } from '@heroicons/vue/24/outline'

  const router = useRouter()

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const lawSpecificMappers = computed(() => caseStore.lawSpecificMappers)
  const pages = computed(() => {
    return [
      {
        title: () => h('span', 'Allgemein'),
        route: ROUTES.APP.CASES.CASE_DETAIL.OVERVIEW.replace(':uuid', caseRecord.value?.uuid)
      },
      {
        title: () => h('span', 'Beteiligte'),
        route: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(':uuid', caseRecord.value?.uuid)
      },
      {
        title: () => h('span', 'Kommunikation'),
        route: ROUTES.APP.CASES.CASE_DETAIL.COMMUNICATION.replace(':uuid', caseRecord.value?.uuid)
      },
      {
        title: () => h('span', 'Dokumente'),
        route: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENTS.replace(':uuid', caseRecord.value?.uuid)
      },
      {
        title: () => h('span', 'Termine'),
        route: ROUTES.APP.CASES.CASE_DETAIL.APPOINTMENTS.replace(':uuid', caseRecord.value?.uuid)
      },
      {
        title: () =>
          h('span', { class: 'flex gap-2 items-center' }, [
            'Rechtsbezogene Daten', // Text for the second page
            h(
              'span',
              { class: 'badge bg-purple' },
              `${lawSpecificMappers.value.filter((mapper) => mapper.data).length}`
            )
          ]),
        route: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(
          ':uuid',
          caseRecord.value?.uuid
        )
      },
      {
        title: () => h('span', 'Timeline'),
        route: ROUTES.APP.CASES.CASE_DETAIL.TIMELINE.replace(':uuid', caseRecord.value?.uuid)
      }
    ]
  })
  const activePage = computed(() => {
    return pages.value.find((page) => page.route === router.currentRoute.value.fullPath)
  })
  onMounted(async () => {
    if (!caseRecord.value) {
      await caseStore.loadCase(router.currentRoute.value.params.uuid.toString())
    }
  })
</script>
<template>
  <SpaCompatComponent v-if="caseRecord">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="py-5">
        <h1 class="text-lg font-semibold text-gray-800 md:text-xl">
          Anfrage von J. Röber
          <span class="px-2 text-gray-500">#J-2024-3</span>
          <span
            class="inline-flex items-center gap-x-1.5 rounded-full bg-teal-100 px-2 py-1 text-xs font-medium
              text-teal-800"
          >
            Neu
          </span>
        </h1>
        <p class="text-sm text-gray-600">
          Terminbuchung
          <ArrowLongRightIcon class="inline size-4" />
          Erstellt am 12. Juni 2024 um 11:55 Uhr
        </p>
      </div>
      <div class="inline-flex items-center gap-x-2">
        <button
          type="button"
          class="inline-flex items-center gap-x-2 rounded-lg border bg-black px-3 py-2 text-sm font-medium text-white
            shadow-sm hover:bg-gray-800 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50"
        >
          <img
            src="https://s3-eu-central-1.ionoscloud.com/jupus/images/actaport_logo.png"
            style="height: 12px"
          />
          Kollisionsprüfung
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-x-2 rounded-lg border bg-black px-3 py-2 text-sm font-medium text-white
            shadow-sm hover:bg-gray-800 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50"
        >
          <img
            src="https://s3-eu-central-1.ionoscloud.com/jupus/images/actaport_logo.png"
            style="height: 12px"
          />
          Akte anlegen
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-x-2 rounded-lg border bg-green-500 px-3 py-2 text-sm font-medium
            text-white shadow-sm hover:bg-green-600 focus:bg-gray-100 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
        >
          Anfrage abschließen
        </button>
        <button
          type="button"
          class="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm
            hover:bg-gray-50 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50"
        >
          <EllipsisVerticalIcon class="size-5" />
        </button>
      </div>
    </div>
    <div class="border-b border-gray-200 px-4 ps-0 dark:border-neutral-700">
      <nav class="flex gap-x-2">
        <button
          v-for="(page, index) in pages"
          :key="page.route"
          class="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap py-2 text-sm hover:text-purple-600
            focus:text-purple-600 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
          :class="[
            page === activePage ? 'border-b-2 border-purple-600 font-semibold text-purple-600' : '',
            index === 0 ? 'pr-1' : 'px-1'
          ]"
          @click="router.push(page.route)"
        >
          <component :is="page.title" />
        </button>
      </nav>
    </div>
    <div class="py-5">
      <router-view></router-view>
    </div>
  </SpaCompatComponent>
</template>
